import { useEffect, useRef, useState, SVGProps, CSSProperties } from 'react';
import Logo from '../GCLOGO.png';
import './LandingComponent2.css';

const TWEAK_DEFAULTS = {
  headlineIdx: 0,
  animStyle: 'watermark' as 'watermark' | 'intercept',
  accentIntensity: 0.28,
  grain: true,
};

// ───────────────────────── Icons ─────────────────────────

const Icon = {
  Shield: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 2 L20 5 V12 C20 17 16.5 21 12 22 C7.5 21 4 17 4 12 V5 Z" />
    </svg>
  ),
  Watermark: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 12h18M3 7h18M3 17h12" />
    </svg>
  ),
  Key: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="8" cy="15" r="4" />
      <path d="M10.85 12.15L19 4M18 5l3 3M15 8l3 3" />
    </svg>
  ),
  Eye: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Radar: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34" />
      <path d="M4 6h.01" />
      <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" />
      <path d="M16.24 7.76A6 6 0 1 0 17.58 14" />
      <path d="M12 18h.01" />
      <path d="M17.99 11.66A6 6 0 0 1 15.77 16.67" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  Chart: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 3v18h18" /><path d="M7 14l3-3 4 4 5-6" />
    </svg>
  ),
  Device: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
    </svg>
  ),
  Check: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  Play: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M8 5v14l11-7z" /></svg>
  ),
  ArrowRight: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Sparkle: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 3v18M3 12h18M7 7l10 10M17 7L7 17" />
    </svg>
  ),
  Lock: (p: SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
};

// ───────────────────────── Decrypt text effect ─────────────────────────

const GLYPHS = '█▓▒░▚▞◼◻◾◇⬟⬡⬢◉01ABCDEF█▓'.split('');

type DecryptTextProps = { text: string; delay?: number; duration?: number };

function DecryptText({ text, delay = 0, duration = 1200 }: DecryptTextProps) {
  const [, forceUpdate] = useState(0);
  const revealedRef = useRef(0);
  const tickRef = useRef(0);

  useEffect(() => {
    revealedRef.current = 0;
    tickRef.current = 0;
    forceUpdate((n) => n + 1);
    const perChar = Math.max(duration / Math.max(text.length, 1), 40);
    let elapsed = -delay;
    const id = setInterval(() => {
      elapsed += 50;
      if (elapsed >= 0) {
        revealedRef.current = Math.min(text.length, Math.floor(elapsed / perChar));
        tickRef.current = (tickRef.current + 1) % 1000;
        forceUpdate((n) => n + 1);
        if (revealedRef.current >= text.length) {
          clearInterval(id);
        }
      }
    }, 50);
    return () => clearInterval(id);
  }, [text, delay, duration]);

  const revealed = revealedRef.current;
  const tick = tickRef.current;

  return (
    <span className="decrypt" aria-label={text}>
      {Array.from(text).map((ch, i) => {
        const done = i < revealed;
        const display = (!done && ch !== ' ') ? GLYPHS[(i + tick) % GLYPHS.length] : ch;
        return (
          <span key={i} className={'decrypt__char' + (done ? '' : ' is-scrambling')}>
            {display === ' ' ? ' ' : display}
          </span>
        );
      })}
    </span>
  );
}

// ───────────────────────── NAV ─────────────────────────

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav className={'nav' + (scrolled ? ' nav--scrolled' : '')}>
      <div className="nav__inner">
        <a href="#" className="nav__brand" style={{ border: 0 }}>
          <img src={Logo} alt="GuardedContent" />
          {/* <span>GuardedContent</span> */}
        </a>
        <div className="nav__links">
          <a href="#features">Platform</a>
          <a href="#how">How it works</a>
          <a href="#pricing">Pricing</a>
          <a href="#creators">Creators</a>
          <a href="#docs">Docs</a>
        </div>
        <div className="nav__cta">
          <a href="#" style={{ border: 0, fontSize: 14, color: 'var(--fg-1)' }}>Sign in</a>
          <button className="btn btn--primary">
            Start your page <Icon.ArrowRight style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
    </nav>
  );
}

// ───────────────────────── HERO ─────────────────────────

const HEADLINES: Array<[string, string]> = [
  ['Share your work.', 'It stays yours.'],
  ['Encrypted by default.', 'Watermarked by play.'],
  ['The next-gen home', 'for protected content.'],
];

type HeroProps = {
  headlineIdx?: number;
  animStyle?: 'watermark' | 'intercept';
  accentIntensity?: number;
};

function Hero({ headlineIdx = 0, animStyle = 'watermark', accentIntensity = 0.28 }: HeroProps) {
  const [tele, setTele] = useState({ sessions: 1247, blocks: 23, latency: 18 });
  useEffect(() => {
    const id = setInterval(() => {
      setTele((t) => ({
        sessions: t.sessions + Math.floor(Math.random() * 3),
        blocks: t.blocks + (Math.random() > 0.8 ? 1 : 0),
        latency: 14 + Math.floor(Math.random() * 8),
      }));
    }, 1600);
    return () => clearInterval(id);
  }, []);

  const [headline1, headline2] = HEADLINES[headlineIdx] || HEADLINES[0];

  return (
    <section
      className="hero grain"
      style={{ ['--accent-intensity' as any]: accentIntensity } as CSSProperties}
    >
      <div className="hero__grid" />
      <div className="hero__glow" />
      <div className="container hero__inner">
        <div>
          <div className="hero__eyebrow">
            <span className="dot" />
            <span>Built for creators selling direct</span>
            <span className="divider" />
            <strong>See why →</strong>
          </div>
          <h1 className="hero__headline">
            <DecryptText text={headline1} duration={900} />
            <br />
            <span className="accent">
              <DecryptText text={headline2} delay={600} duration={1000} />
            </span>
          </h1>
          <p className="hero__sub">
            A creator platform with subscriptions, pay-per-post, and DMs — wrapped in DRM, A/B watermarking,
            and real-time reupload detection. Sell direct to your fans. Keep every pixel yours.
          </p>
          <div className="hero__ctas">
            <button className="btn btn--primary btn--lg">
              Start your page — no card
              <Icon.ArrowRight style={{ width: 16, height: 16 }} />
            </button>
            <button className="btn btn--ghost btn--lg">
              <Icon.Play style={{ width: 14, height: 14 }} /> See a creator page
            </button>
          </div>
          <div className="hero__meta">
            <div className="avatars">
              <div className="avatar" style={{ background: 'linear-gradient(135deg, #E4002B, #7A001A)' }} />
              <div className="avatar" style={{ background: 'linear-gradient(135deg, #D69A3C, #6B4A18)' }} />
              <div className="avatar" style={{ background: 'linear-gradient(135deg, #5B8DEF, #2A4080)' }} />
              <div className="avatar" style={{ background: 'linear-gradient(135deg, #3DA26A, #154a30)' }} />
            </div>
            <span><strong>3,400+ creators</strong> protecting their work today</span>
          </div>
        </div>

        <HeroViz animStyle={animStyle} tele={tele} />
      </div>
    </section>
  );
}

type HeroVizProps = {
  animStyle: 'watermark' | 'intercept';
  tele: { sessions: number; blocks: number; latency: number };
};

function HeroViz({ animStyle, tele }: HeroVizProps) {
  const [wmName] = useState('alex.chen@studio');
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (animStyle !== 'intercept') return;
    const id = setInterval(() => {
      setBlocked(true);
      setTimeout(() => setBlocked(false), 1400);
    }, 4500);
    return () => clearInterval(id);
  }, [animStyle]);

  return (
    <div className="hero__viz">
      <div className="viz-hud viz-hud--tl">
        <span>SEC/A1</span><span className="tick" /><span className="active">● LIVE</span>
      </div>
      <div className="viz-hud viz-hud--tr">
        <span>lic_9f2a4b</span><span className="tick" /><span>AES-256</span>
      </div>
      <div className="viz-hud viz-hud--bl">
        <span>{tele.latency}ms</span><span className="tick" /><span>↗ 1.2gb/s</span>
      </div>
      <div className="viz-hud viz-hud--br">
        <span>64bit · LT-9</span><span className="tick" /><span className="active">✓ SEAL</span>
      </div>

      <div className="viz-player">
        <div className="viz-film" />

        <div className="viz-wm viz-wm--center">GuardedContent</div>

        <div className="viz-wm viz-wm--tl">
          {wmName} · lic_9f2a4b · {new Date().toISOString().slice(0, 19)}
        </div>
        <div className="viz-wm viz-wm--br">
          104.28.5.3 · fp_4c92e · session 0x1f
        </div>

        <div className="viz-scan" />

        <div className={'viz-intercept' + (blocked ? ' show' : '')} />

        <div className="viz-chrome">
          <div className="viz-chrome__play"><Icon.Play style={{ width: 12, height: 12 }} /></div>
          <div className="viz-chrome__bar" />
          <div className="viz-chrome__time">02:14 / 05:30</div>
          <div className="viz-chrome__badge"><span className="dot" />PROTECTED</div>
        </div>
      </div>

      <div className="viz-telemetry">
        <div className="tele-row"><span>sessions</span><strong>{tele.sessions.toLocaleString()}</strong></div>
        <div className="tele-row"><span>intercepts</span><strong className="ok">{tele.blocks}</strong></div>
        <div className="tele-row"><span>latency</span><strong>{tele.latency}ms</strong></div>
        <div className="tele-row"><span>status</span><strong className="ok">● secure</strong></div>
      </div>
    </div>
  );
}

// ───────────────────────── Logo wall ─────────────────────────

function LogoWall() {
  return (
    <section className="logos container">
      <div className="logos__label">Trusted by creators and studios shipping premium work</div>
      <div className="logos__row">
        <div style={{ fontFamily: 'serif', fontStyle: 'italic' }}>Lumen</div>
        <div style={{ letterSpacing: '0.3em' }}>NORTHCUT</div>
        <div style={{ fontFamily: 'var(--font-mono)' }}>{'<kernel/>'}</div>
        <div style={{ fontWeight: 700 }}>MORRISON</div>
        <div>Paper&amp;Ink</div>
        <div style={{ fontFamily: 'var(--font-mono)', letterSpacing: 0 }}>atlas.fm</div>
      </div>
    </section>
  );
}

// ───────────────────────── FEATURES ─────────────────────────

function Features() {
  return (
    <section id="features" className="section zone-light">
      <div className="container">
        <div className="section__head">
          <span className="eyebrow">A platform, not a patch</span>
          <h2>Everything you need to sell. Nothing you need to worry about.</h2>
          <p>Subscriptions, pay-per-post, DMs, and a storefront — wrapped in the protection infrastructure other platforms don't have.</p>
        </div>

        <div className="features">
          <div className="feat feat--wide">
            <div className="feat__glyph"><Icon.Watermark /></div>
            <h3>A/B watermarking</h3>
            <p>Every fan session gets a unique A/B variant sequence baked into the stream. Imperceptible in playback, forensically identifying if a clip shows up where it shouldn't.</p>
            <div className="feat-visual"><div className="fv-wm" /></div>
          </div>

          <div className="feat">
            <div className="feat__glyph"><Icon.Key /></div>
            <h3>Subscriptions & PPV</h3>
            <p>Monthly tiers, one-off unlocks, tips, bundles. Payouts to your bank in 2 days. No per-seat or per-upload fees.</p>
          </div>

          <div className="feat">
            <div className="feat__glyph"><Icon.Lock /></div>
            <h3>Encrypted by default</h3>
            <p>AES-256 DRM, keys rotated per session. Every stream, every download, every DM attachment — locked.</p>
          </div>

          <div className="feat feat--wide">
            <div className="feat__glyph"><Icon.Radar /></div>
            <h3>Reupload detection</h3>
            <p>We crawl 40+ platforms hourly — tube sites, Telegram mirrors, Reddit — comparing perceptual fingerprints. When your content surfaces somewhere you didn't put it, we file takedowns before the first thousand views.</p>
            <div className="feat-visual"><BarChart /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BarChart() {
  const [active, setActive] = useState(18);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % 24), 180);
    return () => clearInterval(id);
  }, []);
  const heights = [20, 35, 28, 52, 40, 60, 48, 72, 58, 80, 65, 88, 70, 92, 78, 68, 82, 60, 74, 55, 48, 38, 30, 22];
  return (
    <div className="fv-bars">
      {heights.map((h, i) => (
        <div key={i} className={'b' + (i <= active ? ' active' : '')} style={{ height: `${h}%` }} />
      ))}
    </div>
  );
}

// ───────────────────────── A/B WATERMARK DEMO ─────────────────────────

function Demo() {
  const [variant, setVariant] = useState<'A' | 'B'>('A');
  const [suspect, setSuspect] = useState<string | null>(null);
  const [matched, setMatched] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setVariant((v) => (v === 'A' ? 'B' : 'A')), 1800);
    return () => clearInterval(id);
  }, []);

  const runMatch = (which: string) => {
    setSuspect(which);
    setMatched(false);
    setTimeout(() => setMatched(true), 1200);
  };

  return (
    <section className="demo section">
      <div className="container">
        <div className="section__head section__head--left">
          <span className="eyebrow">Try it — A/B watermarking</span>
          <h2>Two streams. One leak. We know who.</h2>
          <p style={{ maxWidth: 620 }}>
            Every viewer is served a unique sequence of A and B variants — imperceptible to the eye,
            but forensically distinct. When a leaked copy surfaces, we match the variant sequence
            back to the original session.
          </p>
        </div>

        <div className="demo__grid">
          <div className="demo__player" style={{ cursor: 'default', boxShadow: 'var(--halo-protected)' }}>
            <div className="demo-film" />

            <div style={{
              position: 'absolute', left: '50%', top: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 180, fontWeight: 700,
              color: variant === 'A' ? 'rgba(228,0,43,0.14)' : 'rgba(91,141,239,0.14)',
              fontFamily: 'var(--font-sans)',
              letterSpacing: '-0.05em',
              transition: 'color 400ms var(--ease-out)',
              userSelect: 'none',
            }}>
              {variant}
            </div>

            <div style={{
              position: 'absolute', top: 16, left: 16,
              fontFamily: 'var(--font-mono)', fontSize: 11,
              color: 'rgba(255,255,255,0.5)',
              display: 'flex', gap: 10, alignItems: 'center',
            }}>
              <span>frame 4,281</span>
              <span style={{
                padding: '3px 8px',
                background: variant === 'A' ? 'rgba(228,0,43,0.25)' : 'rgba(91,141,239,0.25)',
                color: variant === 'A' ? '#ff6b7f' : '#9fb8f5',
                border: `1px solid ${variant === 'A' ? 'rgba(228,0,43,0.5)' : 'rgba(91,141,239,0.5)'}`,
                borderRadius: 4, fontWeight: 600,
                transition: 'all 400ms var(--ease-out)',
              }}>VARIANT {variant}</span>
            </div>

            <div className="viz-chrome" style={{ margin: 16, left: 16, right: 16, bottom: 16 }}>
              <div className="viz-chrome__play"><Icon.Play style={{ width: 12, height: 12 }} /></div>
              <div className="viz-chrome__bar" />
              <div className="viz-chrome__time">02:14 / 05:30</div>
              <div className="viz-chrome__badge"><span className="dot" />WATERMARKED</div>
            </div>
          </div>

          <div className="demo__controls">
            <div className="control-card">
              <h4>Two fans, two sequences</h4>
              <p>Each paying session gets a unique A/B fingerprint.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: 'var(--fg-2)', width: 72 }}>fan @mia</span>
                  <span style={{ letterSpacing: '0.15em', color: 'var(--fg-0)' }}>ABBABAAB…</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: 'var(--fg-2)', width: 72 }}>fan @jordan</span>
                  <span style={{ letterSpacing: '0.15em', color: 'var(--fg-0)' }}>BABABAAA…</span>
                </div>
              </div>
            </div>

            <div className="control-card">
              <h4>Found a leak?</h4>
              <p>Match a suspect clip against our variant database.</p>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  className="btn btn--ghost"
                  style={{ flex: 1, height: 32, fontSize: 12 }}
                  onClick={() => runMatch('0x1f')}
                >Match clip #1</button>
                <button
                  className="btn btn--ghost"
                  style={{ flex: 1, height: 32, fontSize: 12 }}
                  onClick={() => runMatch('0x2c')}
                >Match clip #2</button>
              </div>
              {suspect && (
                <div style={{
                  marginTop: 12, padding: '8px 10px',
                  borderRadius: 6,
                  background: matched ? 'rgba(61,162,106,0.12)' : 'rgba(228,0,43,0.12)',
                  border: `1px solid ${matched ? 'rgba(61,162,106,0.4)' : 'rgba(228,0,43,0.4)'}`,
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  color: matched ? 'var(--success)' : '#ff6b7f',
                }}>
                  {matched
                    ? `✓ matched → session ${suspect} · alex.chen@studio`
                    : 'scanning variant sequence…'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── HOW IT WORKS ─────────────────────────

function HowItWorks() {
  return (
    <section id="how" className="section zone-light">
      <div className="container">
        <div className="section__head">
          <span className="eyebrow">How it works</span>
          <h2>Three steps. Minutes, not weeks.</h2>
        </div>
        <div className="steps">
          <div className="step">
            <div className="step__num">01 / UPLOAD</div>
            <h3>Drop a file.</h3>
            <p>We encrypt on the way in. Keys live in our HSM, never in plaintext. Original stays yours.</p>
            <div className="step__anim"><UploadAnim /></div>
          </div>
          <div className="step">
            <div className="step__num">02 / FINGERPRINT</div>
            <h3>Every viewer, unique.</h3>
            <p>We mint an A/B watermark sequence per session — imperceptible in playback, forensically identifying on leak.</p>
            <div className="step__anim"><ConfigAnim /></div>
          </div>
          <div className="step">
            <div className="step__num">03 / SHARE</div>
            <h3>Send a link.</h3>
            <p>Recipients watch in the protected viewer. Every session is watermarked, every pixel accounted for.</p>
            <div className="step__anim"><ShareAnim /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function UploadAnim() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setP((v) => (v >= 100 ? 0 : v + 2)), 80);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 8 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-2-light)', display: 'flex', justifyContent: 'space-between' }}>
        <span>studio-cut-v3.mp4</span>
        <span>{p}%</span>
      </div>
      <div style={{ height: 4, background: 'var(--bg-3-light)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: 'var(--brand-red)', transition: 'width 80ms linear' }} />
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--success)' }}>
        ✓ AES-256 · keys → HSM · original encrypted
      </div>
    </div>
  );
}

function ConfigAnim() {
  const [seq, setSeq] = useState('');
  useEffect(() => {
    const id = setInterval(() => {
      setSeq((s) => (s.length >= 16 ? '' : s + (Math.random() > 0.5 ? 'A' : 'B')));
    }, 180);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 8, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--fg-1-light)' }}>
      <div style={{ color: 'var(--fg-2-light)' }}>session 0x1f · seq</div>
      <div style={{ letterSpacing: '0.2em', color: 'var(--fg-0-light)', fontSize: 13, minHeight: 18 }}>{seq}<span className="blink">|</span></div>
      <div style={{ color: 'var(--success)' }}>✓ fingerprint embedded</div>
    </div>
  );
}

function ShareAnim() {
  return (
    <div style={{ padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 10 }}>
      <div style={{
        padding: '8px 14px', borderRadius: 999, border: '1px solid var(--border-light-strong)',
        fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-0-light)',
        background: 'var(--bg-1-light)',
      }}>
        guard.to/v/9f2a4b
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-2-light)' }}>
        ready in <span style={{ color: 'var(--brand-red)', fontWeight: 600 }}>0.3s</span>
      </div>
    </div>
  );
}

// ───────────────────────── PRICING ─────────────────────────

function Pricing() {
  const [gmv, setGmv] = useState(50000);
  const rate = 0.20;
  const platformFee = Math.round(gmv * rate);
  const toCreator = gmv - platformFee;
  return (
    <section id="pricing" className="section">
      <div className="container">
        <div className="section__head">
          <span className="eyebrow">Pricing</span>
          <h2>We only win when you do.</h2>
          <p>No seats, no uploads, no per-viewer caps. We take a flat percentage of sales made through the platform — everything else is included.</p>
        </div>

        <div className="pricing-card">
          <div className="pricing-card__head">
            <div>
              <div style={{ fontSize: 12, color: 'var(--fg-2)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Platform fee</div>
              <div className="pricing-card__price">
                20<span className="unit">% of transactions</span>
              </div>
            </div>
            <button className="btn btn--primary">Start free <Icon.ArrowRight style={{ width: 14, height: 14 }} /></button>
          </div>

          <label style={{ fontSize: 13, color: 'var(--fg-1)' }}>
            Your monthly sales: <strong style={{ color: 'var(--fg-0)', fontFamily: 'var(--font-mono)' }}>${gmv.toLocaleString()}</strong>
          </label>
          <input
            type="range"
            className="range"
            min="500"
            max="1000000"
            step="500"
            value={gmv}
            onChange={(e) => setGmv(+e.target.value)}
          />
          <div className="slider-row">
            <span>$500</span>
            <span>$1M</span>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
            marginTop: 24, padding: 16,
            borderRadius: 'var(--r-md)',
            background: 'var(--bg-2)',
            border: '1px solid var(--border)',
            fontFamily: 'var(--font-mono)', fontSize: 12,
          }}>
            <div>
              <div style={{ color: 'var(--fg-2)', marginBottom: 4 }}>You keep</div>
              <div style={{ color: 'var(--fg-0)', fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                ${toCreator.toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ color: 'var(--fg-2)', marginBottom: 4 }}>Platform fee</div>
              <div style={{ color: 'var(--brand-red)', fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                ${platformFee.toLocaleString()}
              </div>
            </div>
          </div>

          <ul className="pricing-card__includes" style={{ marginTop: 32 }}>
            {[
              'Unlimited uploads',
              'AES-256 DRM encryption',
              'A/B watermarking',
              'Reupload crawler (40+ sites)',
              'Unlimited team seats',
              'Analytics & session logs',
              'Embed on any domain',
              'Email + Slack alerts',
            ].map((t) => (
              <li key={t}><Icon.Check /> {t}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── TESTIMONIALS ─────────────────────────

function Testimonials() {
  const items = [
    {
      q: 'My top tier doubled in two months. A/B watermarking means I can sleep — if anything leaks, they know we know.',
      name: 'Maya Patel', role: '4.2K subscribers', color1: '#E4002B', color2: '#6B0015',
    },
    {
      q: 'I moved my whole community off three platforms into one. Payouts hit faster, support actually answers, my people actually show up.',
      name: 'Jonas Weber', role: 'Fitness coach · 12K subs', color1: '#D69A3C', color2: '#5C4318',
    },
    {
      q: 'A clip leaked to a tube site and I had the session ID before I finished my coffee. Takedown filed by lunch.',
      name: 'Priya Ramirez', role: 'Creator · $180K/mo', color1: '#5B8DEF', color2: '#1e3a80',
    },
  ];
  return (
    <section className="section zone-light">
      <div className="container">
        <div className="section__head">
          <span className="eyebrow">Field reports</span>
          <h2>From creators already earning here.</h2>
        </div>
        <div className="testimonials">
          {items.map((t, i) => (
            <div className="testimonial" key={i}>
              <p>"{t.q}"</p>
              <div className="testimonial__who">
                <div className="testimonial__avatar" style={{ background: `linear-gradient(135deg, ${t.color1}, ${t.color2})` }} />
                <div>
                  <div className="testimonial__name">{t.name}</div>
                  <div className="testimonial__role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── FAQ ─────────────────────────

function FAQ() {
  const [open, setOpen] = useState<number>(0);
  const items = [
    { q: 'How do I get paid?', a: 'Direct deposit to your bank, every Friday. Payouts typically land in 2 business days. Stripe Connect under the hood — we handle tax forms, chargebacks, and global payments.' },
    { q: 'What content can I post?', a: 'Video, audio, photo sets, text, live streams, and DM-exclusive attachments. Anything legal and yours. Full content policy in our docs.' },
    { q: 'What happens if someone screen-records my content?', a: 'A/B watermark sequences are embedded frame-by-frame in the encoded stream. Screen recordings inherit the signature — if a clip surfaces, we match it back to the paying session in under a minute.' },
    { q: 'Can I bring my existing subscribers over?', a: "Yes. We'll migrate your Patreon, OnlyFans, Gumroad, or Substack audience with a one-click import and pro-rate their existing subscriptions." },
    { q: 'Do I own my content and my audience?', a: "Entirely. Your originals, your subscriber list, your revenue data — exportable any time. We're infrastructure, not a walled garden." },
    { q: "What's the fee?", a: '20% of transactions. No monthly minimum, no per-upload charges, no seat fees. Standard payment processing (~2.9% + 30¢) passes through at cost.' },
  ];
  return (
    <section className="section">
      <div className="container">
        <div className="section__head">
          <span className="eyebrow">Questions</span>
          <h2>Things creators ask before they move.</h2>
        </div>
        <div className="faq">
          {items.map((it, i) => (
            <div key={i} className={'faq__item' + (open === i ? ' open' : '')} onClick={() => setOpen(open === i ? -1 : i)}>
              <div className="faq__q">
                <span>{it.q}</span>
                <span className="faq__icon">+</span>
              </div>
              <div className="faq__a">{it.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── CTA + FOOTER ─────────────────────────

function CTA() {
  return (
    <section className="cta grain">
      <div className="cta__glow" />
      <div className="container cta__inner">
        <h2>Your audience. Your content. Your terms.</h2>
        <p>Sign up in 60 seconds. First payout in a week. We don't charge you a cent until your fans do.</p>
        <div className="cta__buttons">
          <button className="btn btn--primary btn--lg">Start your page <Icon.ArrowRight style={{ width: 16, height: 16 }} /></button>
          <button className="btn btn--ghost btn--lg">Migrate from another platform</button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={Logo} alt="GuardedContent" style={{ height: 26, width: 'auto' }} />
              {/* <span style={{ fontWeight: 600, letterSpacing: '-0.01em' }}>GuardedContent</span> */}
            </div>
            <p className="footer__brand">
              The creator platform with protection built in. Subscriptions, pay-per-post, DMs, and livestreams — all encrypted, all watermarked, all yours.
            </p>
          </div>
          <div className="footer__col">
            <h5>Platform</h5>
            <a href="#">Features</a>
            <a href="#">Pricing</a>
            <a href="#">Payouts</a>
            <a href="#">Security</a>
          </div>
          <div className="footer__col">
            <h5>Creators</h5>
            <a href="#">Migrate from Patreon</a>
            <a href="#">Creator stories</a>
            <a href="#">Referral program</a>
            <a href="#">Support</a>
          </div>
          <div className="footer__col">
            <h5>Company</h5>
            <a href="#">About</a>
            <a href="#">Blog</a>
            <a href="#">Careers</a>
            <a href="#">Contact</a>
          </div>
          <div className="footer__col">
            <h5>Legal</h5>
            <a href="#">Terms</a>
            <a href="#">Privacy</a>
            <a href="#">DMCA</a>
            <a href="#">Licenses</a>
          </div>
        </div>
        <div className="footer__bottom">
          <span>© 2026 GuardedContent</span>
          <span style={{ fontFamily: 'var(--font-mono)' }}><span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-1" /> all systems operational · 23ms</span>
        </div>
      </div>
    </footer>
  );
}

// ───────────────────────── App ─────────────────────────

export default function LandingComponent2() {
  const t = TWEAK_DEFAULTS;
  return (
    <div className={'lp2'}>
      <div className={'page' + (t.grain ? '' : ' grain--off')}>
        <Nav />
        <Hero
          headlineIdx={t.headlineIdx}
          animStyle={t.animStyle}
          accentIntensity={t.accentIntensity}
        />
        <LogoWall />
        <Features />
        <Demo />
        <HowItWorks />
        <Pricing />
        <Testimonials />
        <FAQ />
        <CTA />
        <Footer />
      </div>
    </div>
  );
}
